import { useAuth } from "./useAuth";
import { useCallback } from "react";

/**
 * Finance API 연동 훅
 * - 재무 테이블 관리, 권한 관리, 데이터 CRUD 등 모든 기능 포함
 * - status: true/false 기반 성공여부
 * - denied 플래그 처리
 */
export default function useFinance() {
  const { api } = useAuth();

  // ==================== 권한 관리 ====================

  /** 전체 권한 목록 조회 */
  const fetchGlobalPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/permission/list");
      if (data?.denied) {
        return { ok: false, denied: true, permissions: [] };
      }
      if (!data?.status) {
        return { ok: false, denied: false, permissions: [] };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const permissions = raw.map((p) => ({
        enterprise_id: p.enterprise_id,
        user_id: p.user_id,
        user_name: p.user_name,
        access_level: p.access_level, // "creator" | "non_creator"
      }));
      return { ok: true, denied: false, permissions };
    } catch (e) {
      console.error("fetchGlobalPermissions error:", e);
      return { ok: false, denied: false, permissions: [] };
    }
  }, [api]);

  /** 전체 권한 수정 (마스터만) */
  const updateGlobalPermission = useCallback(
    async (userId, changed) => {
      try {
        const { data } = await api.post("/finance/permission/update", {
          user_id: userId,
          changed,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("updateGlobalPermission error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 테이블별 권한 목록 조회 */
  const fetchTablePermissions = useCallback(
    async (tableId) => {
      try {
        const { data } = await api.get(
          `/finance/table/permission/list?ftable=${tableId}`
        );
        if (data?.denied) {
          return { ok: false, denied: true, permissions: [] };
        }
        if (!data?.status) {
          return { ok: false, denied: false, permissions: [] };
        }
        const raw = Array.isArray(data.data) ? data.data : [];
        const permissions = raw.map((p) => ({
          id: p.id,
          enterprise_id: p.enterprise_id,
          table_schema_id: p.table_schema_id,
          user_id: p.user_id,
          user_name: p.user_name,
          permission_type: p.permission_type, // "owner" | "admin" | "edit" | "view"
          is_me: p.is_me,
        }));
        return { ok: true, denied: false, permissions };
      } catch (e) {
        console.error("fetchTablePermissions error:", e);
        return { ok: false, denied: false, permissions: [] };
      }
    },
    [api]
  );

  // ==================== 테이블 관리 ====================

  /** 테이블 생성 */
  const createTable = useCallback(
    async ({ table_name, description }) => {
      try {
        const { data } = await api.post("/finance/table/create", {
          table_name,
          description: description || "",
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("createTable error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 테이블 수정 */
  const updateTable = useCallback(
    async ({ table_id, table_name, description }) => {
      try {
        const { data } = await api.post("/finance/table/update", {
          table_id,
          table_name,
          description: description || "",
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("updateTable error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 테이블 삭제 */
  const deleteTable = useCallback(
    async (tableId) => {
      try {
        const { data } = await api.post("/finance/table/delete", {
          table_id: tableId,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("deleteTable error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 테이블 목록 조회 */
  const fetchTableList = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/table/list");
      if (data?.denied) {
        return { ok: false, denied: true, tables: [] };
      }
      if (!data?.status) {
        return { ok: false, denied: false, tables: [] };
      }
      const result = data.data || {};
      const tables = Array.isArray(result.items) ? result.items : [];
      return {
        ok: true,
        denied: false,
        tables: tables.map((t) => ({
          id: t.id,
          table_name: t.table_name,
          description: t.description,
          created_at: t.created_at,
          updated_at: t.updated_at,
          owner_id: t.owner_id,
          owner_name: t.owner_name,
          permission_level: t.permission_level,
          row_count: t.row_count || 0,
          column_count: t.column_count || 0,
        })),
        total_count: result.total_count || 0,
      };
    } catch (e) {
      console.error("fetchTableList error:", e);
      return { ok: false, denied: false, tables: [] };
    }
  }, [api]);

  /** 테이블 상세 조회 */
  const fetchTableDetail = useCallback(
    async (tableId) => {
      try {
        const { data } = await api.get(`/finance/table/view?ftable=${tableId}`);
        if (data?.denied) {
          return { ok: false, denied: true, table: null };
        }
        if (!data?.status) {
          return { ok: false, denied: false, table: null };
        }
        const result = data.data || {};
        return {
          ok: true,
          denied: false,
          table: result.table,
          columns: result.columns || [],
          aggregations: result.aggregations || [],
          permission: result.permission,
          statistics: result.statistics || {},
        };
      } catch (e) {
        console.error("fetchTableDetail error:", e);
        return { ok: false, denied: false, table: null };
      }
    },
    [api]
  );

  // ==================== 템플릿 관리 ====================

  /** 템플릿 목록 조회 */
  const fetchTemplates = useCallback(
    async (category) => {
      try {
        const url = category
          ? `/finance/table/template/list?category=${category}`
          : "/finance/table/template/list";
        const { data } = await api.get(url);
        if (!data?.status) {
          return { ok: false, templates: [] };
        }
        const templates = Array.isArray(data.data) ? data.data : [];
        return { ok: true, templates };
      } catch (e) {
        console.error("fetchTemplates error:", e);
        return { ok: false, templates: [] };
      }
    },
    [api]
  );

  /** 템플릿으로 테이블 생성 */
  const createTableFromTemplate = useCallback(
    async ({ template_id, table_name, description }) => {
      try {
        const { data } = await api.post("/finance/table/create_w_tmpl", {
          template_id,
          table_name,
          description: description || "",
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("createTableFromTemplate error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 커스텀 템플릿 생성 */
  const createCustomTemplate = useCallback(
    async ({ table_id, name, category, description, is_public }) => {
      try {
        const { data } = await api.post("/finance/table/template/create", {
          table_id,
          name,
          category,
          description: description || "",
          is_public: !!is_public,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("createCustomTemplate error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 커스텀 템플릿 삭제 */
  const deleteCustomTemplate = useCallback(
    async (templateId) => {
      try {
        const { data } = await api.post("/finance/table/template/delete", {
          template_id: templateId,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("deleteCustomTemplate error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  // ==================== 공유 관리 ====================

  /** 테이블 공유 */
  const shareTable = useCallback(
    async ({ table_id, shares }) => {
      try {
        const { data } = await api.post("/finance/table/share", {
          table_id,
          shares, // [{ user_id, permission: "view" | "edit" | "admin" }]
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("shareTable error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 공유 권한 수정 */
  const updateShareLevel = useCallback(
    async ({ table_id, shares }) => {
      try {
        const { data } = await api.post("/finance/table/share/level/update", {
          table_id,
          shares,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("updateShareLevel error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 공유 제거 */
  const removeShare = useCallback(
    async ({ table_id, remove_ids }) => {
      try {
        const { data } = await api.post("/finance/table/share/ban", {
          table_id,
          remove_ids,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("removeShare error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  // ==================== 컬럼 관리 ====================

  /** 컬럼 추가 */
  const addColumn = useCallback(
    async ({
      table_id,
      display_name,
      data_type,
      is_required,
      width,
      settings,
    }) => {
      try {
        const { data } = await api.post("/finance/table/col/add", {
          table_id,
          display_name,
          data_type,
          is_required: !!is_required,
          width,
          settings: settings || {},
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("addColumn error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 컬럼 수정 */
  const updateColumn = useCallback(
    async ({
      table_id,
      column_id,
      display_name,
      data_type,
      is_required,
      width,
      settings,
    }) => {
      try {
        const { data } = await api.post("/finance/table/col/update", {
          table_id,
          column_id,
          display_name,
          data_type,
          is_required: !!is_required,
          width,
          settings: settings || {},
        });

        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("updateColumn error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 컬럼 삭제 */
  const deleteColumn = useCallback(
    async ({ table_id, column_id }) => {
      try {
        const { data } = await api.post("/finance/table/col/delete", {
          table_id,
          column_id,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("deleteColumn error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 컬럼 재정렬 */
  const reorderColumns = useCallback(
    async ({ table_id, column_orders }) => {
      try {
        const { data } = await api.post("/finance/table/col/reorder", {
          table_id,
          column_orders, // { column_id: order_number }
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("reorderColumns error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  // ==================== 데이터(행) 관리 ====================

  /** 행 추가 */
  const createRow = useCallback(
    async ({ table_id, row_data }) => {
      try {
        const { data } = await api.post("/finance/table/data/add", {
          table_id,
          row_data,
        });
        if (data?.denied) {
          return { ok: false, denied: true, row: null };
        }
        if (!data?.status) {
          return { ok: false, denied: false, row: null };
        }
        return {
          ok: true,
          denied: false,
          row: data.data,
        };
      } catch (e) {
        console.error("createRow error:", e);
        return { ok: false, denied: false, row: null };
      }
    },
    [api]
  );

  /** 행 수정 */
  const updateRow = useCallback(
    async ({ table_id, row_id, row_data }) => {
      console.log(row_data);
      try {
        const { data } = await api.post("/finance/table/data/update", {
          table_id,
          row_id,
          row_data,
        });
        console.log(data);
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("updateRow error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 행 삭제 */
  const deleteRow = useCallback(
    async ({ table_id, row_id }) => {
      try {
        const { data } = await api.post("/finance/table/data/delete", {
          table_id,
          row_id,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("deleteRow error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 행 목록 조회 */
  const fetchRows = useCallback(
    async (tableId) => {
      try {
        const { data } = await api.get(
          `/finance/table/data/view?ftable=${tableId}`
        );
        if (data?.denied) {
          return { ok: false, denied: true, rows: [], columns: [] };
        }
        if (!data?.status) {
          return { ok: false, denied: false, rows: [], columns: [] };
        }
        const result = data.data || {};
        return {
          ok: true,
          denied: false,
          rows: result.rows || [],
          columns: result.columns || [],
          total_count: result.total_count || 0,
        };
      } catch (e) {
        console.error("fetchRows error:", e);
        return { ok: false, denied: false, rows: [], columns: [] };
      }
    },
    [api]
  );

  // ==================== 집계 관리 ====================

  /** 집계 생성 */
  const createAggregation = useCallback(
    async ({ table_id, column_id, aggregation_type, display_name }) => {
      try {
        const { data } = await api.post("/finance/table/aggregation/create", {
          table_id,
          column_id,
          aggregation_type,
          display_name,
        });
        if (data?.denied) {
          return { ok: false, denied: true, aggregation: null };
        }
        if (!data?.status) {
          return { ok: false, denied: false, aggregation: null };
        }
        return {
          ok: true,
          denied: false,
          aggregation: data.data,
        };
      } catch (e) {
        console.error("createAggregation error:", e);
        return { ok: false, denied: false, aggregation: null };
      }
    },
    [api]
  );

  /** 집계 조회 */
  const fetchAggregations = useCallback(
    async (tableId) => {
      try {
        const { data } = await api.get(
          `/finance/table/aggregation/view?ftable=${tableId}`
        );
        if (data?.denied) {
          return { ok: false, denied: true, aggregations: [] };
        }
        if (!data?.status) {
          return { ok: false, denied: false, aggregations: [] };
        }
        return {
          ok: true,
          denied: false,
          aggregations: data.data || [],
        };
      } catch (e) {
        console.error("fetchAggregations error:", e);
        return { ok: false, denied: false, aggregations: [] };
      }
    },
    [api]
  );

  /** 집계 삭제 */
  const deleteAggregation = useCallback(
    async ({ table_id, aggregation_id }) => {
      try {
        const { data } = await api.post("/finance/table/aggregation/delete", {
          table_id,
          aggregation_id,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        console.error("deleteAggregation error:", e);
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  return {
    // 권한 관리
    fetchGlobalPermissions,
    updateGlobalPermission,
    fetchTablePermissions,

    // 테이블 관리
    createTable,
    updateTable,
    deleteTable,
    fetchTableList,
    fetchTableDetail,

    // 템플릿 관리
    fetchTemplates,
    createTableFromTemplate,
    createCustomTemplate,
    deleteCustomTemplate,

    // 공유 관리
    shareTable,
    updateShareLevel,
    removeShare,

    // 컬럼 관리
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,

    // 데이터(행) 관리
    createRow,
    updateRow,
    deleteRow,
    fetchRows,

    // 집계 관리
    createAggregation,
    fetchAggregations,
    deleteAggregation,
  };
}
